import { useCallback, useState } from 'react';
import { isJsonPayload } from '../../../shared/api/payload';
import axios from 'axios';
import { toast } from 'react-toastify';
import { useV3OfferContext, V3_ACTIONS } from '../contexts/V3OfferContext';

// Owns the send-offer modal state machine consumed by both V3FinancePage
// (desktop) and V3FinancePageMobile (mobile). Centralising the open state,
// the seeded fields, and the POST handler ensures the two shells can never
// drift on what the modal contains or what gets submitted.
export default function useV3OfferSend() {
  const { csrfToken, team, offer, dispatch } = useV3OfferContext();

  const [showSendModal, setShowSendModal] = useState(false);
  const [sendTo, setSendTo] = useState('');
  const [sendMessage, setSendMessage] = useState('');
  const [isSendingOffer, setIsSendingOffer] = useState(false);

  const handleSendOffer = useCallback(() => {
    setSendTo(offer?.last_sent_to || offer?.agent_email || '');
    setSendMessage(offer?.email_body || '');
    setShowSendModal(true);
  }, [offer]);

  const handleSubmitSend = useCallback(async (e) => {
    e.preventDefault();
    if (!sendTo || !sendTo.trim()) return;

    setIsSendingOffer(true);
    axios.defaults.headers.common['X-CSRF-TOKEN'] = csrfToken;

    try {
      const response = await axios.post(`/teams/${team.id}/offers/v3/${offer.id}/send_offer.json`, {
        v3_offer: {
          last_sent_to: sendTo,
          email_body: sendMessage,
        },
      });
      if (!isJsonPayload(response.data)) {
        toast.error("Couldn't send this offer. Please reload the page and try again.");
        return;
      }
      dispatch({ type: V3_ACTIONS.SET_OFFER, payload: { ...offer, ...response.data } });
      setSendTo('');
      setSendMessage('');
      setShowSendModal(false);
      toast.success('Offer sent!', {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 3000,
        hideProgressBar: true,
      });
    } catch (error) {
      toast.error('Failed to send offer', {
        position: toast.POSITION.TOP_CENTER,
        draggable: false,
        closeOnClick: false,
        autoClose: 5000,
        hideProgressBar: true,
      });
    } finally {
      setIsSendingOffer(false);
    }
  }, [sendTo, sendMessage, csrfToken, team, offer, dispatch]);

  return {
    showSendModal,
    setShowSendModal,
    sendTo,
    setSendTo,
    sendMessage,
    setSendMessage,
    isSendingOffer,
    handleSendOffer,
    handleSubmitSend,
  };
}
